// @flow

import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { MoreVertIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  IconButton,
  MenuItem,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Menu } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import parentMessages from '../messages';
import DeleteGrossRentChangeModal from './DeleteGrossRentChangeModal';
import useDeleteGrossRentChangeAction from './hooks.useDeleteGrossRentChangeAction';
import messages from './messages';
import type { GrossRentChangeDrawerActionsMenuProps } from './types';

const EDIT_DELETE_SCOPE = ['edit-delete-hud-gross-rent-limits'];

const wrapDisabledMenuItem = (
  menuItem: React$Node,
  disabled: boolean,
  tooltipMessage: React$Node,
) => {
  if (disabled === false) {
    return menuItem;
  }

  return (
    <Tooltip title={tooltipMessage} placement="left" variant="light">
      <span>{menuItem}</span>
    </Tooltip>
  );
};

const GrossRentChangeDrawerActionsMenu = ({
  effectiveDate,
  isLocked = false,
  hasCompletedCertifications = false,
  onEdit,
}: GrossRentChangeDrawerActionsMenuProps) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), []);
  const hasEditDeletePermission = usePermissionsHook({
    scope: EDIT_DELETE_SCOPE,
  });
  const lockedTooltip = <FormattedMessage {...messages.lockedChangeTooltip} />;
  const noPermissionTooltip = (
    <FormattedMessage {...parentMessages.noPermissionTooltip} />
  );
  const isEditDisabled = hasCompletedCertifications || !hasEditDeletePermission;
  const editTooltip = hasCompletedCertifications
    ? lockedTooltip
    : noPermissionTooltip;
  const isDeleteDisabled = isLocked || !hasEditDeletePermission;
  const deleteTooltip = isLocked ? lockedTooltip : noPermissionTooltip;

  const {
    isDeleteModalOpen,
    isSubmitting,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  } = useDeleteGrossRentChangeAction({ effectiveDate });

  const handleEditClick = useCallback(() => {
    setOpen(false);
    if (onEdit) {
      onEdit();
    }
  }, [onEdit]);

  const handleDeleteClick = useCallback(() => {
    setOpen(false);
    openDeleteModal();
  }, [openDeleteModal]);

  const editMenuItem = wrapDisabledMenuItem(
    <MenuItem disabled={isEditDisabled} onClick={handleEditClick}>
      <FormattedMessage {...messages.edit} />
    </MenuItem>,
    isEditDisabled,
    editTooltip,
  );

  const deleteMenuItem = wrapDisabledMenuItem(
    <MenuItem disabled={isDeleteDisabled} onClick={handleDeleteClick}>
      <FormattedMessage {...messages.delete} />
    </MenuItem>,
    isDeleteDisabled,
    deleteTooltip,
  );

  return (
    <>
      <IconButton
        ref={anchorRef}
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((previous) => !previous);
        }}
        sx={{
          color: 'text.secondary',
          p: '10px',
          width: 40,
          height: 40,
        }}
        id={`gross-rent-change-actions-${effectiveDate}`}
        aria-label="Gross rent change actions"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorRef.current} open={open} handleClose={handleClose}>
        {editMenuItem}
        {deleteMenuItem}
      </Menu>
      <DeleteGrossRentChangeModal
        effectiveDate={effectiveDate}
        open={isDeleteModalOpen}
        isSubmitting={isSubmitting}
        onCancel={closeDeleteModal}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default GrossRentChangeDrawerActionsMenu;
